import { useEffect, useState } from 'react';
import { api, ApiError } from '../../lib/api';

/**
 * 财务门控提示（INTEGRAL_ENABLE_FINANCE_READ）。
 *
 * 为什么单列：这些页面在生产环境是**故意不可用**的（本包尚未完成财务验收）。
 * 若把它渲染成普通错误，用户会去反复重试、工程师会去查网络 —— 真因只是一个环境开关。
 */
export function isFinanceGateDisabled(error: unknown): boolean {
  return error instanceof ApiError && (error.code === 'FINANCE_READ_DISABLED' || error.status === 503);
}

export type FinanceGateState = 'unknown' | 'enabled' | 'disabled';

// 进程级缓存：门控是环境属性，不是用户属性，一次会话探一次即可。
// 不放 React state 里做数据源，是为了让多个组件（导航、页面探针）共享同一结论。
let gateCache: FinanceGateState = 'unknown';
let gateProbe: Promise<FinanceGateState> | null = null;

async function probeFinanceGate(): Promise<FinanceGateState> {
  // 首选：权限快照。门控关闭时后端**不声明** attachments 模块 —— 这是同一次门控判定
  // 的另一侧证据（路由 503 / 写权限 403 / 快照删键三处同源）。
  try {
    const snap = await api.get<{ data?: { modules?: Record<string, unknown> } }>('/api/permissions');
    const mods = snap?.data?.modules;
    if (mods && typeof mods === 'object') return 'attachments' in mods ? 'enabled' : 'disabled';
  } catch {
    // 快照不可读（如后端版本未含该字段）→ 走端点探测
  }
  // 回退：附件端点。admin/业务角色在门控关闭时会拿到 503 FINANCE_READ_DISABLED，
  // 开启时返回 200（空列表）。403 属权限问题而非门控问题，按「已启用」处理 ——
  // 该角色本来看不到财务入口，导航由 view 权限过滤兜底。
  try {
    await api.list('/api/attachments', { limit: 1 });
    return 'enabled';
  } catch (e) {
    return isFinanceGateDisabled(e) ? 'disabled' : 'enabled';
  }
}

/**
 * 读取财务门控状态。unknown 表示尚未探测完成 —— 此时按「未启用」渲染更安全：
 * 未启用最多是入口暂时不可见，而已启用却误判会让人以为功能坏了。
 */
export function useFinanceGate(): FinanceGateState {
  const [state, setState] = useState<FinanceGateState>(gateCache);
  useEffect(() => {
    if (gateCache !== 'unknown') { setState(gateCache); return; }
    let alive = true;
    gateProbe = gateProbe || probeFinanceGate();
    gateProbe.then((value) => {
      gateCache = value;
      if (alive) setState(value);
    });
    return () => { alive = false; };
  }, []);
  return state;
}

export function FinanceGateNotice({ detail }: { detail?: string }) {
  return (
    <section className="panel">
      <div className="panel-head">
        <div>
          <h2>该财务能力尚未在本环境启用</h2>
          <div className="desc">受环境开关 INTEGRAL_ENABLE_FINANCE_READ 控制（默认关闭）</div>
        </div>
      </div>
      <div className="panel-body">
        <div className="notice info" style={{ display: 'block' }}>
          <div style={{ fontWeight: 650, marginBottom: 6 }}>这不是故障，是刻意的默认状态</div>
          <div>
            该页面属于「Factura 承接」的隔离建设范围，需财务验收通过后才会在正式环境开启。
            当前生产环境保持关闭，既有 Factura 不受影响。
            {detail ? <><br />接口返回：{detail}</> : null}
          </div>
        </div>
      </div>
    </section>
  );
}